'use client'

import { useQuery, keepPreviousData } from '@wiggot/libs/react-query'

import { legacyApiAuth } from '@wiggot/apis'

import { rulesKeys, VERSION_API_WIGGOT_LEGACY } from '../constants'
import { zRules } from '../schema'

import type { Rules } from '../schema'

import type { UseConfigQueryOptions } from '@wiggot/libs/react-query'

/**
 * `useRules` is a custom hook that fetches and returns the rules data from the Wiggot legacy API.
 * It uses the `useQuery` hook from `react-query` to fetch the data and manage the query state.
 * The `queryOptions` parameter allows you to customize the query configuration.
 *
 * The hook returns an object containing the `data` and the rest of the properties returned by `useQuery`.
 *
 * @param {UseConfigQueryOptions<Rules>} queryOptions - The options for the query configuration.
 * @returns {Object} An object containing the rules data and the rest of the properties returned by `useQuery`.
 *
 */
export function useRules(queryOptions: UseConfigQueryOptions<Rules> = {}) {
  const { data, ...rest } = useQuery({
    queryKey: rulesKeys.getRules.queryKey,
    queryFn: async () => {
      const response = await legacyApiAuth.get(
        `/${VERSION_API_WIGGOT_LEGACY}/rules`
      )

      return zRules().parse(response)
    },
    placeholderData: keepPreviousData,
    ...queryOptions
  })

  return { data, ...rest }
}
